import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { addMonths, currentMonth, type MonthKey, monthLabel } from './format';

/**
 * "‹ شتنبر 2026 ›": previous month on the right (RTL), no future months.
 *
 * Changing the month must be obvious: the title flashes, `loading` shows
 * «جاري تحميل شهر …» until the page has that month's data, and a month other
 * than the current one is marked, with a way back (the choice is remembered
 * across reloads, so it is easy to forget).
 */
export function MonthSwitch({
  month,
  onChange,
  loading = false,
}: {
  month: MonthKey;
  onChange: (month: MonthKey) => void;
  /** The page still shows another month's data. */
  loading?: boolean;
}) {
  const now = currentMonth();
  return (
    <div className="month-switch-block">
      <div className="month-switch">
        <Button
          size="large"
          icon={<RightOutlined />}
          aria-label="الشهر السابق"
          onClick={() => onChange(addMonths(month, -1))}
        />
        {/* key: a new element per month, so the flash replays on each change */}
        <h1 key={month} className="month-title" aria-live="polite">
          {monthLabel(month)}
        </h1>
        <Button
          size="large"
          icon={<LeftOutlined />}
          aria-label="الشهر التالي"
          disabled={month >= now}
          onClick={() => onChange(addMonths(month, 1))}
        />
      </div>
      {loading ? (
        <div className="month-status is-loading">
          <Spin size="small" /> جاري تحميل شهر {monthLabel(month)}…
        </div>
      ) : (
        month !== now && (
          <div className="month-status is-past">
            <span>شهر سابق، وليس الشهر الحالي</span>
            <Button size="small" type="link" onClick={() => onChange(now)}>
              الرجوع إلى {monthLabel(now)}
            </Button>
          </div>
        )
      )}
    </div>
  );
}

/**
 * Whether a urql result is the answer to `variables`. While a new month
 * loads, urql still returns the previous month's result: pages must not
 * present it as the new month.
 */
export function isResultFor(
  operation: { variables?: unknown } | undefined,
  variables: Record<string, unknown>,
): boolean {
  if (!operation) return false;
  const actual = (operation.variables ?? {}) as Record<string, unknown>;
  return Object.keys(variables).every((key) => actual[key] === variables[key]);
}

/**
 * The selected month, this month by default, kept in sessionStorage under
 * `key` so it survives a reload of the tab.
 */
export function useStoredMonth(key: string) {
  const [month, setMonth] = useState<MonthKey>(() => {
    try {
      const value = sessionStorage.getItem(key);
      if (value && /^\d{4}-\d{2}$/.test(value)) return value as MonthKey;
    } catch {
      // storage unavailable (private mode): fall back to this month
    }
    return currentMonth();
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(key, month);
    } catch {
      // not critical
    }
  }, [key, month]);

  return [month, setMonth] as const;
}
